import {
  Box,
  Button,
  ButtonText,
  CloseIcon,
  FormControl,
  FormControlError,
  FormControlErrorText,
  Heading,
  HStack,
  Icon,
  Input,
  InputField,
  Pressable,
  Text,
  Toast,
  ToastTitle,
  useToast,
  View,
  VStack,
} from '@gluestack-ui/themed';
import { useMutation } from '@tanstack/react-query';
import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useWindowDimensions } from 'react-native';

import { PrimaryButton } from '../components/buttons/PrimaryButton';
import { SecundaryButton } from '../components/buttons/SecundaryButton';
import { Carousel } from '../components/camera/Carousel';
import { Title } from '../components/Titles/Title';
import { OrganismSelectionCards } from '../components/analysis/OrganismSelectionCards';
import { HypothesisSection } from '../components/analysis/HypothesisSection';
import type { UserHypothesis } from '../components/analysis/HypothesisSection';
import { AnalysisStepper } from '../components/analysis/AnalysisStepper';
import { TranslationContext } from '../contexts/translations/Translation';
import { UserContext } from '../contexts/User';
import type { IAnalysisFormPageProps } from '../routes/Routes';
import { Routes } from '../routes/Routes';
import { styles } from '../theme/styles';
import { uploadToS3 } from '../utils/AwsS3Utils';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { FormDatePicker } from '../components/form/FormDatePicker';
import { FormTemperature } from '../components/form/FormTemperature';
import { AIModelDTO } from '../generated/client-api-client/models/AIModelDTO';
import { AnalysisDTO } from '../generated/client-api-client/models/AnalysisDTO';
import { AnalysisService } from '../generated/client-api-client/services/AnalysisService';
import { CreateAnalysisDTO } from '../generated/client-api-client';
import { COLORS } from '../theme/colors';

type AnalysisFormValues = Partial<CreateAnalysisDTO & AnalysisDTO & {
  coloration: string;
  zoom: number;
}>;

const ZOOM_OPTIONS = [60, 100] as const;

export const EditAnalysisFormPage: FC<IAnalysisFormPageProps> = (props: IAnalysisFormPageProps) => {
  const { navigation } = props;
  const { analysisList } = props.route.params;
  const [currentAnalysisIndex, setCurrentAnalysisIndex] = useState<number>(0);
  const [formList, setFormList] = useState<AnalysisFormValues[]>(analysisList ?? []);
  const { i18n } = useContext(TranslationContext);
  const user = useContext(UserContext);

  const { close, show } = useToast();

  const validationSchema = Yup.object().shape({
    id: Yup.string(),
    sampleId: Yup.string()
      .required(i18n.t('form.required'))
      .min(1, i18n.t('form.minString', { value: 1 }))
      .max(9, i18n.t('form.maxString', { value: 9 })),
    receptionDate: Yup.string()
      .required(i18n.t('form.required'))
      .test('is-valid-date', i18n.t('form.dateBeforeThanToday'), (value: string) => new Date(value) < new Date()),
    sampleTemperature: Yup.string()
      .required(i18n.t('form.required'))
      .test('is-valid-temperature', i18n.t('form.temperatureFormat'), (value) => {
        if (!value) return false;
        return /^[+-]?\d+(\.\d+)?$/.test(value);
      }),
    coloration: Yup.string().required(i18n.t('form.required')),
    zoom: Yup.number().oneOf([60, 100]).required(i18n.t('form.required')),
    properties: Yup.object().shape({
      color: Yup.string().optional(),
      basicMedium: Yup.string().optional(),
      hemolyticProperty: Yup.string().optional(),
    }),
  });

  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const mappedValues = {
        ...values,
        receptionDate: values.receptionDate,
        sampleTemperature: Number(values.sampleTemperature),
      } as AnalysisFormValues;
      const id = await saveAnalysis(mappedValues);
      const updatedFormList = formList.map((analysis, index) => {
        if (index === currentAnalysisIndex) {
          return { ...mappedValues, id };
        }
        return analysis;
      });
      if (currentAnalysisIndex === formList.length - 1) {
        if (formList.length === 1) {
          return navigation.navigate(Routes.ANALYSIS, { analysisId: id });
        }
        return navigation.navigate(Routes.ANALYSIS_LIST, { watchAnalysisIds: updatedFormList.map((analysis) => analysis.id!) });
      }

      const nextIndex = currentAnalysisIndex + (1 % formList.length);
      setFormList(updatedFormList);
      setCurrentAnalysisIndex(nextIndex);
      return Promise.resolve();
    },
    initialValues: {
      ...analysisList[0],
    } as AnalysisFormValues,
  });

  const [selectedOrganisms, setSelectedOrganisms] = useState<Set<string>>(new Set());
  const [hypothesis, setHypothesis] = useState<UserHypothesis>({});
  const needsHypothesis = selectedOrganisms.has('bacteria');
  // Methylene blue has no Gram reaction, so Gram isn't part of the hypothesis then.
  const isMethyleneBlue = formik.values.coloration === 'methylene_blue';
  const hypothesisComplete = Boolean(hypothesis.shape) && (isMethyleneBlue || Boolean(hypothesis.gram));

  const toggleOrganism = (key: string) => {
    setSelectedOrganisms((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const getAimoDetectionModelId = useCallback(() => {
    const models: AIModelDTO[] = user?.currentWorkspace?.aiModels ?? [];
    return (
      // Prefer the new 4-head model (detection_students: stain/organism/gram/shape)
      // so the verrou reveal gets real gram/shape. Falls back to the original
      // order if the workspace isn't linked to it — so rollback is just
      // unlinking detection_students (behaviour returns to stage1_binaire).
      models.find((m) => m.tag === 'detection_students')?.id ??
      models.find((m) => m.tag === 'stage1_binaire')?.id ??
      models.find((m) => m.tag === 'aimo_detection')?.id ??
      models.find((m) => m.tag === 'default')?.id ??
      models[0]?.id
    );
  }, [user?.currentWorkspace?.aiModels]);

  const showHypothesisRequiredToast = () => {
    show({
      id: 'hypothesisRequired',
      placement: 'bottom left',
      duration: 6_000,
      render: ({ id }) => (
        <Toast action="warning" variant="accent" alignItems="center" gap={10}>
          <ToastTitle>{i18n?.t('verrou.hypothesis.required')}</ToastTitle>
          <Pressable onPress={() => close(id)}>
            <Icon as={CloseIcon} />
          </Pressable>
        </Toast>
      ),
    });
  };

  const showStartAnalysisJobErrorToast = () => {
    show({
      id: 'startAnalysisJobError',
      placement: 'bottom left',
      duration: 10_000,
      render: ({ id }) => (
        <Toast action="error" variant="accent" alignItems="center" gap={10}>
          <ToastTitle>{i18n?.t('page.analysis-details.toast.start-analysis-job-error')}</ToastTitle>
          <Pressable onPress={() => close(id)}>
            <Icon as={CloseIcon} />
          </Pressable>
        </Toast>
      ),
    });
  };

  const { mutateAsync: saveAnalysis, isPending: isSavingAnalysis } = useMutation({
    mutationFn: async (data: AnalysisFormValues) => {
      if (!data.id) {
        const response = await AnalysisService.analysisControllerCreate(user!.currentWorkspace!.id!, {
          name: data?.name ?? '',
          sampleId: data?.sampleId ?? '',
          receptionDate: new Date(data?.receptionDate ?? new Date()).toISOString(),
          properties: {
            ...(data.properties ?? {}),
            organisms: Array.from(selectedOrganisms).join(','),
            ...(() => {
              // methylene blue has no Gram reaction — never store a Gram guess for it
              const h = data.coloration === 'methylene_blue' ? { shape: hypothesis.shape } : hypothesis;
              return h.gram || h.shape ? { userHypothesis: h } : {};
            })(),
          },
          sampleTemperature: data.sampleTemperature ?? 0,
          coloration: data.coloration,
          zoom: data.zoom,
          aiModelId: getAimoDetectionModelId(),
        });

        await uploadToS3(data.imageUrl!, response.uploadURL);
        try {
          await AnalysisService.analysisControllerStartAnalysis(user!.currentWorkspace!.id!, response.id!);
        } catch (error) {
          showStartAnalysisJobErrorToast();
        }
        return response.id;
      }
      if (
        data.name === formList[currentAnalysisIndex].name &&
        JSON.stringify(data.properties) === JSON.stringify(formList[currentAnalysisIndex].properties) &&
        data.receptionDate === formList[currentAnalysisIndex].receptionDate &&
        data.imageUrl === formList[currentAnalysisIndex].imageUrl &&
        data.sampleId === formList[currentAnalysisIndex].sampleId &&
        data.coloration === formList[currentAnalysisIndex].coloration &&
        data.zoom === formList[currentAnalysisIndex].zoom
      ) {
        return data.id;
      }
      await AnalysisService.analysisControllerUpdate(user!.currentWorkspace!.id!, data.id ?? '', data);
      return data.id;
    },
  });

  useEffect(() => {
    if (currentAnalysisIndex) {
      formik.setValues(formList[currentAnalysisIndex]);
    }
  }, [currentAnalysisIndex]);

  const isDisabled = !!formik.values?.id;
  const { width } = useWindowDimensions();
  const isNarrow = width < 860;

  const colorationOptions = [
    { label: i18n.t('page.new-analysis.form.coloration-gram'), value: 'gram' },
    { label: i18n.t('page.new-analysis.form.coloration-methylene-blue'), value: 'methylene_blue' },
  ];

  return (
    <View style={styles.layout}>
      <VStack>
        {/* Header row: title + date/time chip OUTSIDE the card, matching AnalysisDetailsPage */}
        <Box display="flex" flexDirection="row" justifyContent="space-between" flexWrap="wrap" alignItems="center">
          <Title title={i18n?.t('page.analysis-details.title')} />
          {!isNarrow && (
            <Box style={{ backgroundColor: 'white', borderRadius: 10, padding: 10, shadowColor: '#171717', shadowOffset: { width: -2, height: 4 }, shadowOpacity: 0.2, shadowRadius: 3 }}>
              <Text>
                {new Date(formik.values.createdAt!).toLocaleDateString(i18n.locale)} | {new Date(formik.values.createdAt!).toLocaleTimeString(i18n.locale)}
              </Text>
            </Box>
          )}
        </Box>
        <HStack style={{ marginVertical: 10, gap: 20, marginTop: 8, flexWrap: 'wrap' }}>
          <VStack style={{ flexGrow: 1, minWidth: 280, maxWidth: isNarrow ? undefined : 450 }}>
            <Box
              style={{
                marginTop: 12,
                backgroundColor: 'white',
                borderRadius: 10,
                shadowColor: '#171717',
                shadowOffset: { width: -2, height: 4 },
                shadowOpacity: 0.2,
                shadowRadius: 3,
                padding: 16,
              }}
            >
              <Box style={{ aspectRatio: '1/1', borderRadius: 6, overflow: 'hidden' }}>
                <Carousel
                  disableControls={true}
                  overrideCurrentPhotoIndex={currentAnalysisIndex}
                  onPhotoChange={setCurrentAnalysisIndex}
                  photoUriList={analysisList.map((analysis) => analysis.imageUrl!)}
                />
              </Box>
            </Box>
          </VStack>

          <VStack
            style={{
              backgroundColor: 'white',
              borderRadius: 10,
              flex: 1.5,
              minWidth: 320,
              paddingHorizontal: isNarrow ? 16 : 32,
              paddingVertical: 20,
              shadowColor: '#171717',
              shadowOffset: { width: -2, height: 4 },
              shadowOpacity: 0.2,
              shadowRadius: 3,
            }}
          >
            <HStack alignItems="flex-start">
              <Box flex={1} />
              <AnalysisStepper currentStep={0} />
              <Box flex={1} />
            </HStack>
            <VStack>
              <Heading size="md" style={{ marginBottom: 16, marginTop: 32 }}>
                {i18n?.t('page.new-analysis.form.sample-information-section-label')}
              </Heading>

              {/* Row 1: Sample ID + Reception date */}
              <HStack gap={16} style={{ marginBottom: 8, flexWrap: 'wrap' }}>
                <FormControl
                  flex={1}
                  style={{ minWidth: 160 }}
                  isRequired
                  isInvalid={Boolean(formik.getFieldMeta('sampleId').error)}
                  isDisabled={isDisabled}
                >
                  <FormControl.Label style={{ marginBottom: 4 }}>
                    <Text>{i18n?.t('page.new-analysis.form.sample-id-label')}</Text>
                  </FormControl.Label>
                  <Input>
                    <InputField
                      maxLength={9}
                      value={formik.values.sampleId ?? ''}
                      onChangeText={formik.handleChange('sampleId')}
                      onBlur={formik.handleBlur('sampleId')}
                    />
                  </Input>
                  <FormControlError>
                    <FormControlErrorText>{formik.getFieldMeta('sampleId').error}</FormControlErrorText>
                  </FormControlError>
                </FormControl>

                <FormControl
                  flex={1}
                  style={{ minWidth: 160 }}
                  isRequired
                  isInvalid={Boolean(formik.getFieldMeta('receptionDate').error)}
                  isDisabled={isDisabled}
                >
                  <FormControl.Label style={{ marginBottom: 4 }}>
                    <Text>{i18n?.t('page.new-analysis.form.sample-received-date-label')}</Text>
                  </FormControl.Label>
                  <FormDatePicker
                    value={formik.values.receptionDate ? new Date(formik.values.receptionDate) : undefined}
                    onChange={(date) => formik.setFieldValue('receptionDate', date?.toISOString())}
                    filterDate={(date: Date) => new Date() > date}
                  />
                  <FormControlError>
                    <FormControlErrorText>{formik.getFieldMeta('receptionDate').error}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              </HStack>

              {/* Row 2: Temperature + Coloration + Zoom */}
              <HStack gap={16} style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                <FormControl
                  flex={1}
                  style={{ minWidth: 120 }}
                  isRequired
                  isInvalid={Boolean(formik.getFieldMeta('sampleTemperature').error)}
                  isDisabled={isDisabled}
                >
                  <FormControl.Label style={{ marginBottom: 4 }}>
                    <Text>{i18n?.t('page.new-analysis.form.sample-temperature-label')}</Text>
                  </FormControl.Label>
                  <FormTemperature
                    value={formik.values.sampleTemperature ? String(formik.values.sampleTemperature) : ''}
                    onChange={formik.handleChange('sampleTemperature')}
                  />
                  <FormControlError>
                    <FormControlErrorText>{formik.getFieldMeta('sampleTemperature').error}</FormControlErrorText>
                  </FormControlError>
                </FormControl>

                <FormControl
                  flex={1}
                  style={{ minWidth: 140 }}
                  isRequired
                  isInvalid={Boolean(formik.getFieldMeta('coloration').error)}
                  isDisabled={isDisabled}
                >
                  <FormControl.Label style={{ marginBottom: 4 }}>
                    <Text>{i18n?.t('page.new-analysis.form.coloration-label')}</Text>
                  </FormControl.Label>
                  <HStack gap={8} flexWrap="wrap">
                    {colorationOptions.map((opt) => {
                      const isActive = formik.values.coloration === opt.value;
                      return (
                        <Button
                          key={opt.value}
                          onPress={() => !isDisabled && formik.setFieldValue('coloration', opt.value)}
                          isDisabled={isDisabled}
                          bg={isActive ? COLORS.ACCENT[100] : 'transparent'}
                          borderWidth={1}
                          borderColor={COLORS.ACCENT[100]}
                          rounded="$lg"
                          flex={1}
                          style={{ minWidth: 120 }}
                          $hover-bg={isActive ? COLORS.ACCENT[110] : COLORS.NEUTRAL[60]}
                        >
                          <ButtonText style={{ color: isActive ? COLORS.NEUTRAL[0] : COLORS.ACCENT[100] }} numberOfLines={1}>
                            {opt.label}
                          </ButtonText>
                        </Button>
                      );
                    })}
                  </HStack>
                  <FormControlError>
                    <FormControlErrorText>{formik.getFieldMeta('coloration').error}</FormControlErrorText>
                  </FormControlError>
                </FormControl>

                <FormControl
                  flex={1}
                  style={{ minWidth: 120 }}
                  isRequired
                  isInvalid={Boolean(formik.getFieldMeta('zoom').error)}
                  isDisabled={isDisabled}
                >
                  <FormControl.Label style={{ marginBottom: 4 }}>
                    <Text>{i18n?.t('page.new-analysis.form.zoom-label')}</Text>
                  </FormControl.Label>
                  <HStack gap={8}>
                    {ZOOM_OPTIONS.map((zoomValue) => {
                      const isActive = formik.values.zoom === zoomValue;
                      return (
                        <Button
                          key={zoomValue}
                          onPress={() => !isDisabled && formik.setFieldValue('zoom', zoomValue)}
                          isDisabled={isDisabled}
                          bg={isActive ? COLORS.ACCENT[100] : 'transparent'}
                          borderWidth={1}
                          borderColor={COLORS.ACCENT[100]}
                          rounded="$lg"
                          flex={1}
                          $hover-bg={isActive ? COLORS.ACCENT[110] : COLORS.NEUTRAL[60]}
                        >
                          <ButtonText style={{ color: isActive ? COLORS.NEUTRAL[0] : COLORS.ACCENT[100] }}>
                            {zoomValue}X
                          </ButtonText>
                        </Button>
                      );
                    })}
                  </HStack>
                  <FormControlError>
                    <FormControlErrorText>{formik.getFieldMeta('zoom').error}</FormControlErrorText>
                  </FormControlError>
                </FormControl>
              </HStack>

              <VStack marginTop={8} marginBottom={12}>
                <OrganismSelectionCards
                  selected={selectedOrganisms}
                  onToggle={toggleOrganism}
                  coloration={formik.values.coloration}
                />
              </VStack>

              {needsHypothesis && (
                <VStack marginBottom={12}>
                  <HypothesisSection value={hypothesis} onChange={setHypothesis} isDisabled={isDisabled} showGram={!isMethyleneBlue} />
                </VStack>
              )}
            </VStack>
            <div id="calendar-portal"></div>
            <HStack justifyContent="space-evenly" flexWrap="wrap">
              <SecundaryButton
                content={i18n?.t('common.cancel')}
                minWidth={150}
                style={{ margin: 10 }}
                onPress={async () => {
                  navigation.goBack();
                }}
              />
              <SecundaryButton
                content={i18n.t('page.new-analysis.form.previous-button-label')}
                minWidth={150}
                style={{ margin: 10 }}
                isDisable={currentAnalysisIndex === 0}
                onPress={async () => {
                  const updatedFormList = formList.map((analysis, index) => {
                    if (index === currentAnalysisIndex) {
                      return { ...formik.values };
                    }
                    return analysis;
                  });
                  const previousIndex = (currentAnalysisIndex - 1 + formList.length) % formList.length;
                  setFormList(updatedFormList);
                  setCurrentAnalysisIndex(previousIndex);
                }}
              />
              <PrimaryButton
                content={currentAnalysisIndex === formList.length - 1 ? i18n.t('page.new-analysis.form.save-form-button-label') : i18n.t('page.new-analysis.form.next-button-label')}
                minWidth={150}
                onPress={async () => {
                  if (needsHypothesis && !hypothesisComplete) {
                    showHypothesisRequiredToast();
                    return;
                  }
                  formik.handleSubmit();
                }}
                style={{ margin: 10 }}
                isLoading={isSavingAnalysis}
              />
            </HStack>
          </VStack>
        </HStack>
      </VStack>
    </View>
  );
};
